import { useMutation } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useNotify, useTranslate } from 'ra-core';
import { updateOfferNegotiation } from '../../../dataProvider/dataProvider';
import { Offer } from '../types';
import BaseNegotiationModal from './BaseNegotiationModal';
import TermsCheckbox from '../../../components/ui/TermsCheckbox';
import SuccessButton from '../../../components/ui/SuccesButton';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DefaultAlert from '../../../components/ui/DefaultAlert';
import { useWatch } from 'react-hook-form';
import { DataProviderWithCustomMethods } from '../../../types';

type Props = {
  offer: Offer;
  open: boolean;
  onClose: () => void;
  refetchOffer: () => void;
  isAccepted?: boolean;
  setIsAccepted?: (isAccepted: boolean) => void;
};

function AcceptOfferModal({
  offer,
  open,
  onClose,
  refetchOffer,
  isAccepted,
  setIsAccepted,
}: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<DataProviderWithCustomMethods>();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Record<string, any>) =>
      updateOfferNegotiation('publisher-accepted', data),
    onSuccess: () => {
      refetchOffer();
      setIsAccepted?.(true);
    },
    onError: error =>
      notify(
        <DefaultAlert
          severity="error"
          alertTitle={t('offer.acceptModal.notification.error')}
          alertDescription={error?.message}
        />
      ),
  });

  const handleSubmit = (formData: any) => {
    mutate({
      parent_id: offer.last_negotiation_id,
      accepted_terms: formData.accepted_terms,
    });
  };

  if (isAccepted) {
    return (
      <BaseNegotiationModal
        open={open}
        onClose={onClose}
        title={t('offer.acceptModal.confirmation.title')}
        onSubmit={undefined}
        actions={
          <Button onClick={onClose} variant="default">
            {t('offer.acceptModal.confirmation.button')}
          </Button>
        }
      >
        <p>{t('offer.acceptModal.confirmation.description')}</p>
      </BaseNegotiationModal>
    );
  }

  return (
    <BaseNegotiationModal
      open={open}
      onClose={onClose}
      title={t('offer.acceptModal.title')}
      onSubmit={handleSubmit}
      actions={<AcceptFormActions isPending={isPending} />}
    >
      <div className="flex flex-col items-start gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            void dataProvider
              .previewBlob(`/purchase-orders/preview`, {
                offer_id: offer.id,
              })
              .catch((err: any) => {
                console.error(err);
                notify(err.message, { type: 'error' });
              });
          }}
        >
          {`Preview purchase order`}
        </Button>
        <TermsCheckbox source="accepted_terms" />
      </div>
    </BaseNegotiationModal>
  );
}

export default AcceptOfferModal;

const AcceptFormActions = ({ isPending }: { isPending: boolean }) => {
  const t = useTranslate();
  const accepted_terms = useWatch({ name: 'accepted_terms' });

  return (
    <SuccessButton
      type="submit"
      disabled={!accepted_terms || isPending}
      size="lg"
      className="w-full"
    >
      <CheckCircle className="size-4" />
      {t('offer.acceptModal.button')}
    </SuccessButton>
  );
};
